import { FC } from 'react';
import UniversalLink from 'src/common/components/UniversalLink';
import { GMB_SUPPORT_EMAIL, PATHS, SERVICE_FEES } from 'src/common/constants';
import styles from 'src/content/ContentPages.module.scss';

const Terms: FC = () => (
  <main className={styles.root}>
    <h1 className={styles.title}>Please review the Terms of Use.</h1>
    <h2 className={styles.subTitle}>EFFECTIVE FEBRUARY 09 2026</h2>
    <h3>Your Relationship with Getmyboat</h3>
    <p>
      Your use of Getmyboat&apos;s products, software, services, website and any mobile applications
      (collectively &quot;Services&quot;) is subject to the terms of this Terms of Use Agreement
      (this &quot;Agreement&quot;). Getmyboat means Getmyboat, Inc. d/b/a Getmyboat (herein referred
      to as &quot;Getmyboat,&quot; &quot;us,&quot; &quot;we,&quot; or &quot;our&quot;). If you are
      using the Services on behalf of a corporation or other legal entity, &quot;you&quot; means the
      entity, and the user is warranting that he or she has the rights to bind the entity. By using
      the Services, you are agreeing to and are subject to the following terms and conditions, as
      well as our
      {' '}
      <UniversalLink
        href={PATHS.PRIVACY_POLICY}
        className={styles.link}
      >
        Privacy Policy
      </UniversalLink>
      , herein incorporated by reference (collectively, the &quot;Terms&quot;). If you do not agree
      to all of the Terms you are not authorized to use Getmyboat&apos;s Services.
    </p>

    <p>
      You accept the Terms by: (i) clicking to accept or agree to the Terms, where this option is
      made available to you by Getmyboat in the user interface for any Service; or, (ii) by actually
      using the Services. In the case of (ii), you understand and agree that Getmyboat treats your
      use of the Services as acceptance of the Terms from that point onward.
    </p>

    <p>
      Getmyboat reserves the right, at its discretion, to change, modify, add or remove any of the
      Terms, in whole or in part, at any time. Please check the Services and the Terms periodically
      for changes. Your use of the Services after such modified Terms are posted will mean that you
      accept such Terms.
    </p>

    <p>
      <strong>
        GETMYBOAT IS A VENUE AND IS NOT A PARTY TO ANY RENTAL AGREEMENT OR OTHER TRANSACTION BETWEEN
        USERS OF THE SERVICES.
      </strong>
    </p>

    <p>
      <strong>Getmyboat is Only a Venue. </strong>
      Getmyboat acts as a venue for users of the Services as renters (&quot;Renters&quot;) and boat
      owners (&quot;Members&quot;) (Members and Renters, each a &quot;User&quot; and together,
      &quot;Users&quot;) to view and post available boats to facilitate the rental of such boats.
      Getmyboat is not a party to any actual agreement or transaction between Renters and Members
      even though we may from time to time provide tools that relate to a booking, such as a tool to
      enable a Renter to enter into a transaction to rent a specific boat directly from a Member and
      payment services to facilitate the booking. As a result, Getmyboat has no control over the
      accuracy or correctness of the content or information provided or used by such Renters and
      Members. Any part of an actual or potential transaction between a Renter and a Member,
      including the condition, quality, safety or legality of the boats advertised, the truth or
      accuracy of the listings (including the content thereof or any review related thereto), the
      ability of Members to rent a boat to a Renter, or the ability of Renters to pay for or operate
      boat rentals are solely the responsibility of each User. Member acknowledges and agrees that
      Member is solely responsible for the compliance and safety of the Renter(s) who rent
      Member&apos;s boat(s).
    </p>

    <p>
      You acknowledge and agree to take all reasonable precautions in ensuring the accuracy and
      reliability of any User generated content. We are also not responsible for the condition of
      any boats listed on the Services or the compliance with laws, rules or regulations that may be
      applicable to boat operation, maintenance, rental or charter in any jurisdiction. As between
      Getmyboat and Members, Member acknowledges and agrees that Member is solely responsible for
      ensuring compliance with laws, rules or regulations that may be applicable to boat operation,
      maintenance, rental or charter in the jurisdiction in which Member operates or is otherwise
      located. Renters should satisfy themselves that any boat rented complies with any legal or
      safety requirements for such rental, and Renters and Members have exclusive responsibility for
      such compliance. Without limiting the foregoing, Members and Renters are solely responsible
      for their own compliance with any legal and/or safety requirements in place in your applicable
      jurisdiction in light of applicable health and/or travel advisories. Members are responsible
      for determining if renting a boat is permissible and, if so, for providing their boat in
      sanitary condition for Renters and with appropriate sanitation supplies for Renters to clean
      the boat after use. Renters are responsible for returning any rented boat in sanitary
      condition using the sanitation supplies provided by the Member.
    </p>

    <p>
      <strong>Separate Agreements. </strong>
      Users acknowledge and agree that you will be required to enter into a separate agreement
      and/or waiver, directly between the Renter and Member, prior to making a booking or purchasing
      a product or service and such agreement and/or waiver may place additional restrictions on
      your booking, product, or service. Any separate agreements and/or waivers relating to the
      rental of a boat or related products or services are under the sole control of any Member or
      provider and Renter.
    </p>

    <p>
      <strong>Release and Indemnity. </strong>
      IN THE EVENT OF A DISPUTE BETWEEN A RENTER AND A MEMBER, MEMBER AND RENTER, JOINTLY AND
      SEVERALLY, HEREBY AGREE TO INDEMNIFY, RELEASE, DEFEND AND HOLD HARMLESS GETMYBOAT AND ITS
      DIRECTORS, OFFICERS, AGENTS AND EMPLOYEES (COLLECTIVELY, THE “GETMYBOAT PARTIES”) FROM ANY
      CLAIMS, CAUSES OF ACTION, DEMANDS, RECOVERIES, LOSSES, DAMAGES (ACTUAL AND CONSEQUENTIAL),
      FINES, PENALTIES AND OTHER COSTS OR EXPENSES, OF ANY KIND OR NATURE, INCLUDING BUT NOT LIMITED
      TO REASONABLE LEGAL AND ACCOUNTING FEES (COLLECTIVELY, THE “CLAIMS”), KNOWN AND UNKNOWN,
      ARISING OUT OF OR IN ANY WAY CONNECTED WITH SUCH DISPUTES, INCLUDING, WITHOUT LIMITATION,
      DISPUTES ARISING OVER BREACH OF THIS AGREEMENT, BREACH OF ANY SEPARATE AGREEMENT OR
      TRANSACTION, AND/OR THE SUBSTITUTION OF RENTAL UNITS. FURTHER, MEMBER AND RENTER EACH HEREBY
      AGREE TO INDEMNIFY, RELEASE, DEFEND AND HOLD HARMLESS THE GETMYBOAT PARTIES FROM ANY CLAIMS
      BROUGHT AS A RESULT OF MEMBER AND/OR RENTER&apos;S (1) BREACH OF THE TERMS, (2) VIOLATION OF
      ANY LAW OR THE RIGHTS OF A THIRD PARTY, AND (3) USE OF THE SERVICES.
    </p>

    <p>
      <strong>Insurance. </strong>
      Getmyboat is not a party to any contract for insurance facilitated through any link to a third
      party insurance provider advertising on the Services, and is not acting on behalf of any
      insurer, any broker or agent, any insured or any claimant in any contract for insurance
      facilitated through such link. Insurance coverage may be provided through a third-party
      independent insurance producer not affiliated with Getmyboat. Getmyboat does not act as a
      broker, agent or consultant in the sale of insurance, is not endorsing or recommending any
      particular insurer or terms of coverage, and is merely providing a link for you, if you wish,
      to transact a contract for insurance with a third-party provider. Any contract for insurance
      is solely between you, as the insured, and such insurer as you and the third-party insurance
      provider agree shall act as the insurer. Getmyboat has no control over and makes no
      representations regarding the coverage of any insurance you may purchase from a third-party
      insurance provider. You acknowledge and agree that Getmyboat assumes no responsibility or
      liability for any claims or disputes arising from, or related to, a contract for insurance
      facilitated through such link. You also acknowledge and agree that Getmyboat shall not be
      included as a party to any such claims or disputes.
    </p>

    <h3>Your Use of Services</h3>

    <p>
      <strong>Conditions of Use. </strong>
      As a condition of your use of the Services, you expressly represent and warrant that (i) if
      you will be operating a boat, you are responsible for ensuring that you are sufficiency
      skilled in the operation of the boat and legally authorized to operate a boat in the state in
      which you are renting and you will not allow any other occupants of the boat to operate the
      boat unless they are each of sufficient skill and legally authorized to operate a boat in the
      state in which you are renting; (ii) you possess the legal authority to create a binding legal
      obligation; (iii) you will use the Services in accordance with the Terms; (iv) you will only
      use the Services to post or view, as applicable, available boat rentals or transact legitimate
      boat rentals for you or for another person for whom you are legally authorized to act with
      other Users; (v) you will inform such other persons about the terms and conditions that apply
      to the boat rental you have made on their behalf, including all laws, regulations, rules and
      restrictions applicable thereto; (vi) if you are booking a rental on behalf of another person,
      you are individually responsible for ensuring that such other person is a party to the
      separate agreement for the rental of the boat; (vii) all information supplied by you to
      Getmyboat is true, accurate, current and complete, (viii) if you have a Getmyboat account, you
      will safeguard your account information, (ix) you are and will remain in compliance with all
      applicable laws, rules and regulations related to boat operation, maintenance, rental or
      charter, and (x) if you are listing a boat for rental, you are either (a) the owner of the
      boat, or (b) a representative authorized to act on behalf of the owner of the boat. You may
      not authorize others to use your user account, and you may not assign or otherwise transfer
      your user account to any other person or entity. If you reside in a jurisdiction that
      restricts the use of the Services because of age, or restricts the ability to enter into
      agreements such as this one due to age, you must abide by such age limits and you must not use
      the Services. Without limiting the foregoing, the Services are not available to children
      (persons under the age of 18). By using the Services, you represent and warrant that you are
      at least 18 years old. Getmyboat retains the right at its sole discretion to deny access to
      the Services to anyone at any time and for any reason, including, but not limited to, for
      violation of the Terms.
    </p>

    <p>
      Sharing contact information such as, email address, phone number, website address, company
      name, social media accounts or profiles, pickup locations, contact information to other
      members, or any other form to connect directly with users outside of Getmyboat is strictly
      prohibited. Getmyboat has the sole discretion to monitor and block all content that it deems
      to be used to circumvent Getmyboat or violates any other terms of use. Failing to abide by the
      terms will result in an account suspension and removal from the platform. You agree that you
      will not try to circumvent Getmyboat by any means which includes but is not limited to
      conversations posted to Getmyboat&apos;s messaging platform, boat listings, public and private
      reviews, and images uploaded.
    </p>

    <p>You agree that you will only use the Services for lawful purposes.</p>

    <p>
      Please keep in mind that we will treat anyone who uses your user name and password as
      &quot;you.&quot; We will provide this user with all of the rights and privileges that we
      provide to you, and we will hold you responsible for the activities of the person using your
      password. Therefore, we recommend that you maintain your user name and password in confidence,
      and that you refrain from disclosing this information to anyone who might &quot;pretend&quot;
      to be you with respect to the Services. We also ask that you notify us immediately if you
      suspect that someone is using your user name and/or password or otherwise obtaining access to
      the Services in this or any other inappropriate manner.
    </p>

    <p>
      <strong>Payment Services. </strong>
      While Getmyboat is not a party to any agreement between you and any Member, Renter
      acknowledges and agrees that Getmyboat may act as the Member&apos;s payment agent for the
      purposes of collecting payment for bookings made through the Services. Renter&apos;s provision
      of financial information to us for purposes of payment is governed by our Privacy Policy.
    </p>

    <p>
      <strong>Establishing Payment Account. </strong>
      As a Renter, you are responsible for opening an account, providing payment authorizations for
      bookings and paying for Services according to these Terms, as well as others which may be
      included at the time of booking. You agree to provide all requested information, including a
      credit card to be used for pre-authorization/reserve and payment/capture for Services. Payment
      for a booking will be made by Getmyboat directly to the Member who posted the listing.
    </p>

    <p>
      By providing your credit card information to us, you agree that we are authorized to charge
      your card with the total cost of the rental, taxes and any required deposit, as described
      below. We will run a pre-authorization for the full amount of the booking on the credit card
      the Renter supplies at the time of booking. You agree that we can charge a nominal amount to
      your credit card to verify the validity of the credit card information you provide. If the
      Member accepts the booking, the Renter&apos;s credit card will be charged in full for the
      amount previously described, although if the Member advises us of a change in delivery
      location following the booking and that change results in an increase in any applicable taxes
      we are required to charge and collect or if there is increase in the amount to taxes imposed
      at the time of booking versus the date of delivery, you expressly authorize us to charge such
      credit card for the excess and if that card is not honored at the time Getmyboat will collect
      such amount from the Member and the Member will collect such amount from you before delivery
      of the rental to you and your failure to pay the Member for such amount may result in denial
      of access to the rental and forfeiture of your deposit. You expressly authorize
      Getmyboat&apos;s service provider, Dwolla, Inc., to originate credit transfers to your
      financial institution account. Getmyboat may also use a service provider to store your credit
      card information to facilitate payments as described herein and you expressly authorize such
      storage. Getmyboat shall ensure that any service provider it uses to process or store your
      credit card information is bound by obligations to protect such information commensurate with
      our Privacy Policy.
    </p>

    <p>
      <strong>Deposits. </strong>
      Renters are responsible for leaving the rented boat in the same condition it was in before you
      rented it. Renters acknowledge and agree that you are responsible for all acts and omissions
      of you and your guests on the boat that affect the condition of the boat. In the event a
      Member claims and provides evidence of damage to the boat, you agree that Getmyboat shall be
      entitled to charge a deposit to your credit card to cover the cost of the damage, as
      established after reasonable investigation. We will notify you of any damage claim made by a
      Member related to your rental and you will have an opportunity to respond, as set forth in
      such notice. We will only charge a deposit to Renter&apos;s credit card after the rental is
      complete if the Member has made a claim and the deposit amount has been deemed appropriate
      after investigation of the claim.
    </p>

    <p>
      <strong>Service Charge. </strong>
      We charge a service fee of up to
      {' '}
      {SERVICE_FEES.RENTER_PERCENT}
      % of the total rental and a flat $20 USD service fee (i.e. the
      booking and any additional expenses such as taxes we are required to collect) for facilitating
      the rental and this amount will be reflected in the final amount charged to Renter&apos;s
      credit card. We reserve the right to round up amounts to the nearest whole dollar (for
      example, $202.60 will be rounded to $203). We will process any necessary currency conversion
      (Getmyboat charges in US dollars unless otherwise specified) based upon Renter&apos;s currency
      on the day the credit card is charged and we will charge a conversion fee of 3% of the total
      rental for currency conversions. We reserve the right to adjust the conversion rate based upon
      when the payout is made to the Member. The service fee and any conversion fee constitute
      &quot;Transaction Fees.&quot; Getmyboat reserves the right to adjust its Transaction Fees at
      any time. Any Fee changes will be effective the next time you book a rental after such fee
      change goes into effect (i.e., rentals already booked will be subject to Fees as established
      at the time of booking).
    </p>

    <p>
      <strong>Payments to Members. </strong>
      Payment to Members for accepted bookings will be made net 30 days from the date of the
      completed reservation. Notwithstanding, in limited circumstances, on a case-by-case basis, in
      its sole discretion, Getmyboat may elect to pay a Member a portion of the accepted booking
      after the reservation is made and the remaining portion of the accepted booking net 30 days
      from the date of the completed (fully paid) reservation. If, after Member has been paid any
      portion of the accepted booking by Getmyboat, an accepted booking is canceled or terminated
      for any reason by either Member or Renter before the reservation is completed, Member shall
      return to Getmyboat all sums advanced by Getmyboat for such reservation immediately (within
      five (5) days) upon receiving written demand for same. Member further agrees that Getmyboat is
      not liable for any damages Member may incur as a result of such cancellation and/or having to
      return such funds. If Member fails to timely refund said sums, Getmyboat shall also be
      entitled to interest on said sums at the rate of 18% per annum, or the highest rate allowed by
      law, whichever is lower, and all of Getmyboat&apos;s attorneys&apos; fees and costs of court.
    </p>

    <p>
      If a Member does not respond to a booking inquiry, Getmyboat reserves the right to transfer
      the booking and associated payment to another Member on Renter&apos;s behalf. Getmyboat
      reserves the right to hold payment to Members if Getmyboat suspects there has been fraud or
      other suspicious activity in connection with the rental or payment. Unacceptable payment
      methods that may be posted on the Services may result in the immediate removal of the
      non-conforming listing from the Services without notice to the Member and without refund.
      Unacceptable payment methods are those methods that may pose a risk of fraud, identify theft,
      or breach of privacy.
    </p>

    <p>
      <strong>Substitution of Rental Units. </strong>
      Renter understands and acknowledges that boats/other water craft reserved may, from time to
      time, be subsequently damaged or otherwise become in need of repair before the reservation is
      commenced. If a Member is unable to provide the reserved unit to Renter due to it being
      unavailable, Member may provide Renter with a substitute unit that is similar in age, size,
      model, and value as the reserved boat/other water craft. Member shall provide Renter with
      notice of the need for a substitution of unit(s) as soon as reasonably possible, as well as
      provide Renter with pictures and a description of the substitute unit upon request. If Member
      is unable to provide Renter with a similar substitute unit (as described herein), Renter may
      elect to cancel the reservation any time before it is commenced and, as its sole remedy,
      obtain a refund in accordance with the Refund Policy as if the Member had canceled the
      reservation. Renter shall not be entitled to any refund, and shall be obligated to pay all
      sums owed, if Member offers Renter a similar unit (as described herein) and Renter
      nevertheless refuses to complete and/or terminates the reservation. If there is a dispute
      between Renter and Member as to whether the substitute unit offered by Member is sufficiently
      similar to the unit reserved by Renter, Getmyboat shall make the final determination, in its
      sole discretion, and Renter and Member agree to abide by Getmyboat&apos;s determination.
    </p>

    <p>
      <strong>
        ANY DIRECT OR INDIRECT EXCHANGE OF MONEY BETWEEN RENTERS AND MEMBERS OTHER THAN PAYMENT
        THROUGH GETMYBOAT IS A VIOLATION OF THESE TERMS.
      </strong>
    </p>

    <p>
      <strong>Taxes. </strong>
      The rental/booking fee and any additional expenses may be subject to applicable local, federal
      and state taxes. Commencing with bookings made on and after date set forth after
      &quot;Effective&quot; set forth on the first page above, Getmyboat will in some locations, as
      Getmyboat phases in its tax collection and reporting system, charge and collect from the
      Renter all applicable taxes Getmyboat is required to collect and which are imposed by a
      governmental authority on the rental amount and other charges that are included in the base on
      which such taxes are imposed and that payable by the Renter through the Services. In some
      jurisdictions, Getmyboat may not begin collecting on the Effective date shown above or in
      others may not be required to collect taxes, and as provided below it is the Member&apos;s
      responsibility to collect and remit any such taxes imposed on the rental amount that Getmyboat
      does not charge and collect. To the extent charged and collected from the Renter by Getmyboat,
      the Member should not charge and collect the same amount of such taxes from the Renter.
      Getmyboat will make the determination of the applicable amount to be charged based on the zip
      code of the Member on file with Getmyboat unless and until the Member advises Getmyboat in
      writing of a change the location of where the boat will be delivered to a Renter. If the
      change in location results in an increase in the amount of applicable taxes that should have
      been charged to and collected from the Renter, the Renter&apos;s credit card will be charged
      for such excess.
    </p>

    <p>
      Except to the extent provided herein or in the Member Interface Agreement, the Member
      accepting the booking is solely responsible for the collection, reporting, and payment of
      applicable taxes where those taxes are not being collected and remitted by Getmyboat.
      Getmyboat will charge applicable taxes to Renter&apos;s credit card at the time of booking and
      later in the event circumstances require a change. To the extent not charged and collected by
      Getmyboat, Members are responsible for determining applicable taxes and should consult with
      your own legal/tax adviser about any tax questions in this regard. For Members based in the
      European Union, it is your responsibility to include VAT in the price of the booking and you
      will be responsible for reporting VAT.
    </p>

    <p>
      <strong>Cancellation and Refund Policy. </strong>
      If Member cancels an accepted rental before the rental is commenced, Renter shall be refunded
      all sums paid to Getmyboat for the rental, other than the Transaction Fee and any taxes
      Getmyboat remitted to a taxing authority prior to the cancellation.
    </p>

    <p>
      If Renter cancels an accepted rental before the rental is commenced, Getmyboat shall return to
      Renter a “Cancellation Refund,” as specified below. The Transaction Fee and any applicable
      taxes collected by Getmyboat and remitted to the taxing authority or authorities before the
      cancellation will not automatically be refunded to the Renter. The amount of the Cancellation
      Refund is determined by the Member&apos;s cancellation policy set forth in the Renter&apos;s
      separate rental agreement with the Member, which shall be one of the following three policies,
      as chosen by the Member:
    </p>

    <strong>Flexible</strong>
    <blockquote>
      <ul>
        <li>
          100% of the rental price if canceled more than 24 hours prior to commencement of the
          rental; and
        </li>
        <li>50% of the rental price if canceled within 24 hours of commencement of the rental.</li>
      </ul>
    </blockquote>

    <strong>Moderate</strong>
    <blockquote>
      <ul>
        <li>
          100% of the rental price if canceled more than 7 days prior to commencement of the
          rental;
        </li>
        <li>
          75% of the rental price if canceled less than 7 days but more than 48 hours prior to
          commencement of the rental; and
        </li>
        <li>
          50% of the rental price if canceled less than 48 hours but more 24 hours prior to
          commencement of the rental.
        </li>
      </ul>
    </blockquote>

    <strong>Strict</strong>

    <blockquote>
      <ul>
        <li>
          75% of the rental price if canceled more than 7 days prior to commencement of the rental;
        </li>
        <li>
          50% of the rental price if canceled less than 7 days but more than 48 hours prior to
          commencement of the rental; and
        </li>
        <li>
          25% of the rental price if canceled less than 48 hours but more 24 hours prior to
          commencement of the rental.
        </li>
      </ul>
    </blockquote>

    <p>
      If, for any reason, the Renter&apos;s separate rental agreement with the Member (a) does not
      contain a cancellation policy or (b) the cancellation policy therein differs from the
      foregoing cancellation policy options, then the amount of the Cancellation Refund will be
      determined in accordance with the “Strict” cancellation policy set-forth above. Renters are
      further subject to any additional cancellation terms set forth in the terms of each listing.
      To the extent the cancellation terms of a particular listing conflicts with the cancellation
      terms set forth herein, the cancellation terms set forth herein control.
    </p>

    <p>
      If Getmyboat issues a Cancellation Refund to Renter that is less than 100% of the rental
      price, Members will be credited with the non-refunded portion of such rental price on the
      Member&apos;s next rental that is successfully completed through Getmyboat.
    </p>

    <p>
      <strong>Renter Weather Cancellation Policy. </strong>
      Boating is a weather-dependent activity, and the safety of all participants is the top
      priority. Getmyboat does not make weather-related determinations for trips. The decision
      to cancel, delay, or proceed with a booking due to weather is at the sole discretion of
      the owner or captain.
    </p>

    <p>
      <strong>Captain Discretion. </strong>
      Owners or captains are responsible for evaluating weather conditions
      and determining whether a trip can proceed safely. This includes factors such as: high
      winds, heavy rain or storms, unsafe water conditions, or local advisories/warnings.
      If the owner or captain deems the conditions unsafe, they may cancel or reschedule
      the trip at their discretion.
    </p>

    <p>
      <strong>If Your Trip Is Canceled Due to Weather. </strong>
      If the captain cancels your trip prior to
      departure due to weather, you are entitled to either: (1) A full refund; or (2) The
      option to reschedule, if agreed upon with the owner.
    </p>

    <p>
      Getmyboat will facilitate refunds as needed, but all final decisions on weather
      cancellations and rescheduling are made by the owner or captain.
    </p>

    <p>
      <strong>If You Choose Not to Go. </strong>
      If the trip is still deemed safe and you choose to cancel
      due to personal weather concerns, standard cancellation terms will apply, and a
      refund is not guaranteed.
    </p>

    <p>
      <strong>Owner Weather Policy. </strong>
      Owners are required to make weather-related safety decisions in accordance with
      Getmyboat&apos;s Owner Weather Cancellation Policy, as set forth in the Member Interface
      Agreement.
    </p>

    <p>
      <strong>Owner Cancellation Policy. </strong>
      Owners are required to comply with Getmyboat&apos;s Owner Cancellation Policy, as
      set forth in the Member Interface Agreement, which governs acceptable reasons for
      cancellation, required communication standards, and potential penalties for avoidable
      cancellations.
    </p>

    <p>
      <strong>Owner Cancellation. </strong>
      Transaction Fees will never be refunded. Getmyboat will charge a fee for any reservation that
      is canceled after both parties have agreed on a booking. The following is a breakdown of
      cancellation fees based on the total price of rental:
    </p>

    <p>
      Under $100: Fee is $5
      <br />
      Over $100 but below $250: Fee is $20
      <br />
      Over $250: Fee is $50
      <br />
    </p>

    <p>
      The Member will be debited the cancellation fees on the next rental that is successfully
      completed through Getmyboat. Renters are subject to the cancellation terms set forth in the
      terms of each listing. Additionally, if a Member cancels a rental for any reason other than
      weather or another circumstance effecting the safety of the Renter, such Member (rather than
      the Renter) will be charged the Transaction Fee for the rental, as set forth above.
    </p>

    <p>
      <strong>Chargeback Policy. </strong>
      If Renter disagrees with any charges, before any dispute is lodged with Renter&apos;s credit
      card company, Renter shall provide Getmyboat with a detailed written explanation of such
      disputed sum within fourteen (14) days of the charge, and the parties shall engage in a
      discussion related to same. FAILURE TO PROVIDE TIMELY NOTICE OF SAID DISPUTE VOIDS ANY CLAIM
      OR DISPUTE. At Getmyboat&apos;s request, Renter shall provide any additional written
      explanation or details and/or supporting documentation, in the format desired by Getmyboat.
      RENTER SHALL NOT DISPUTE VALID CHARGES ASSESSED IN ACCORDANCE WITH THESE TERMS OF USE.
    </p>

    <p>
      Member understands and acknowledges that Getmyboat acts merely as a conduit for reservations
      and payments between Renters and Members, and Member assumes all rental payment risk. IF A
      RENTER OBTAINS A CREDIT CARD CHARGEBACK AGAINST GETMYBOAT, FOR ANY REASON, AND GETMYBOAT HAS
      ALREADY PAID ALL OR A PORTION OF SUCH AMOUNT TO MEMBER, THEN MEMBER SHALL IMMEDIATELY (WITHIN
      FIVE (5) DAYS UPON RECEIVING WRITTEN DEMAND FOR SAME) RETURN TO GETMYBOAT ALL SUCH AMOUNTS.
    </p>

    <p>
      <strong>Use of Services. </strong>
      The content and information on Getmyboat&apos;s Services (including, but not limited to, price
      and availability of boats listed in any inventory database), as well as the infrastructure
      used to provide such content and information, is proprietary to Getmyboat, its licensors, its
      suppliers and providers. While you may make limited copies of your booking documents (and
      related documents) for boat rentals booked through the Services, you agree not to and you have
      no right to otherwise modify, copy, distribute, transmit, display, perform, reproduce,
      publish, license, create derivative works from, transfer, or sell or re-sell any information,
      software, content, products, or services obtained from or through the Services. Additionally,
      you agree not to:
    </p>

    <blockquote>
      <ul>
        <li>
          use the Services or its contents for any commercial purpose without our express written
          permission;
        </li>
        <li>
          send or store infringing, obscene, threatening, libelous, or otherwise unlawful or
          tortious material, including material harmful to children or violative of third party
          privacy rights;
        </li>
        <li>
          send or store material containing software viruses, worms, Trojan horses or other harmful
          computer code, files, scripts, agents or programs;
        </li>
        <li>
          interfere with or disrupt the integrity or performance of the Services or the data
          contained therein;
        </li>
        <li>
          attempt to gain unauthorized access to the Services or its related systems or networks;
        </li>
        <li>
          make any speculative, false, or fraudulent reservation or any reservation in anticipation
          of demand;
        </li>
        <li>
          access, monitor or copy any content or information of the Services using any robot,
          spider, scraper or other automated means or any manual process for any purpose without our
          express written permission;
        </li>
        <li>
          violate the restrictions in any robot exclusion headers on the Services or bypass or
          circumvent other measures employed to prevent or limit access to the Services;
        </li>
        <li>
          take any action that imposes, or may impose, in Getmyboat&apos;s discretion, an
          unreasonable or disproportionately large load on Getmyboat&apos;s infrastructure;
        </li>
        <li>
          deep-link to any portion of the Services (including, without limitation, the purchase path
          for any boat rental reservation) for any purpose without our express written permission;
          or
        </li>
        <li>
          &quot;frame&quot;, &quot;mirror&quot; or otherwise incorporate any part of the Services
          into any other website or service without our prior written authorization.
        </li>
      </ul>
    </blockquote>

    <h3>AGREEMENT TO ARBITRATE DISPUTES</h3>
    <p>
      <strong>
        IMPORTANT: PLEASE REVIEW THE ARBITRATION AGREEMENT SET FORTH BELOW CAREFULLY, AS IT WILL
        REQUIRE YOU TO RESOLVE DISPUTES WITH GETMYBOAT ON AN INDIVIDUAL BASIS THROUGH FINAL AND
        BINDING ARBITRATION. BY ENTERING THIS AGREEMENT, YOU EXPRESSLY ACKNOWLEDGE THAT YOU HAVE
        READ AND UNDERSTAND ALL OF THE TERMS OF THIS AGREEMENT AND HAVE TAKEN TIME TO CONSIDER THE
        CONSEQUENCES OF THIS IMPORTANT DECISION.
      </strong>
    </p>

    <p>
      By agreeing to the Terms, you agree that you are required to resolve any claim that you may
      have against Getmyboat on an individual basis in arbitration, as set forth below. This will
      preclude you from bringing any class, collective, or representative action against Getmyboat,
      and also preclude you from participating in or recovering relief under any current or future
      class, collective, consolidated, or representative action brought against Getmyboat by someone
      else.
    </p>

    <p>
      Opt-out of Agreement to Arbitrate. You can decline this agreement to arbitrate by emailing
      {' '}
      {GMB_SUPPORT_EMAIL}
      {' '}
      and saying “I opt out of arbitration” in the email within 30 days of first
      registering your account.
    </p>

    <p>
      You and Getmyboat agree that any dispute, claim or controversy (&quot;Claim&quot;) arising out
      of or relating to this agreement between you and Getmyboat, including (without limitation)
      Claims based on contract, tort (including intentional torts), fraud, agency, negligence,
      statutory or regulatory provisions or any other source of law and (except as otherwise
      specifically provided in this Agreement) Claims relating to the existence, breach,
      termination, enforcement, interpretation or validity of this agreement (including the validity
      of this arbitration clause), shall be settled by binding arbitration administered by the
      American Arbitration Association, www.adr.org, 1-800-778-7879 (the “Administrator”), and not
      in a court of law (excluding solely non-monetary injunctive or equitable relief as provided
      below). The Administrator provides information about arbitration, its arbitration rules and
      procedures, fee schedule and claims forms on its web site or by mail as set forth above. The
      Administrator will apply the rules and procedures in effect and applicable to the claim at the
      time the arbitration is filed. The Claim will be heard before a single arbitrator. The
      arbitration will not be consolidated with any other arbitration proceedings. The arbitrator
      shall not have any authority to entertain a claim, or to award any relief, on behalf of or
      against anyone other than a named party to the arbitration proceeding. The Administrator shall
      resolve each dispute in accordance with applicable law. You further agree that judgment on the
      award rendered by the arbitrator(s) may be entered in any court having jurisdiction thereof.
    </p>

    <p>
      You consent to electronic service of process, with service to be made to the email address we
      have on record for your account.
    </p>

    <p>
      YOU ACKNOWLEDGE AND AGREE THAT YOU AND GETMYBOAT ARE EACH WAIVING THE RIGHT TO A TRIAL BY JURY
      OR TO PARTICIPATE AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION OR
      REPRESENTATIVE PROCEEDING. UNLESS BOTH YOU AND GETMYBOAT OTHERWISE AGREE IN WRITING, ANY
      ARBITRATION OR OTHER LEGAL PROCEEDING WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND NOT IN
      A CLASS, COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. Notwithstanding mandatory
      arbitration as provided above, you and Getmyboat each retain the right to seek solely
      non-monetary injunctive or other equitable relief in a court of competent jurisdiction to
      prevent the actual or threatened infringement, misappropriation or violation of a party&apos;s
      copyrights, trademarks, trade secrets, patents or other intellectual property rights.
    </p>

    <p>
      You agree that any and all communications and evidence related to any dispute ultimately
      resolved by arbitration or mediation with the Administrator arising out of or relating to this
      agreement shall be held and will remain confidential, and that you will not take any action
      that will harm the reputation of any of the other parties to the arbitration or mediation or
      Getmyboat or which would reasonably be expected to lead to unwanted or unfavorable publicity
      to and of the parties, Getmyboat or other entities involved in or incidental to the
      arbitration or mediation. You understand that those parties and entities include but are not
      limited to the claimant, respondent, witnesses, and Getmyboat.
    </p>

    <p>
      Notwithstanding any choice of law or other provision in this agreement parties agree and
      acknowledge that this arbitration clause evidences a transaction involving interstate commerce
      and that the Federal Arbitration Act, 9 U.S.C. § 1 et seq. (“FAA”), will govern its
      interpretation and enforcement and proceedings pursuant thereto. It is the intent of the
      parties that the FAA and Administrator Rules shall preempt all state laws to the fullest
      extent permitted by law. If the FAA and Administrator Rules are found to not apply to any
      issue that arises under this arbitration clause or the enforcement thereof, then that issue
      shall be resolved under the laws of the state of Georgia.
    </p>

    <h3>Identity Verification</h3>

    <p>
      User verification on the Internet is difficult and we cannot, and do not, assume any
      responsibility for the confirmation of each Renter or Member&apos;s purported identity.
      Notwithstanding the foregoing, you agree that you will provide us or the Member with whom you
      book a rental whatever proof of identity we may reasonably request.
    </p>

    <p>
      You agree to (i) keep your password and online ID for both your Getmyboat account and your
      email account secure and strictly confidential, (ii) notify us immediately and select a new
      online ID and password if you believe your password for either your Getmyboat account or your
      email account may have become known to an unauthorized person, and (iii) notify us immediately
      if you are contacted by anyone requesting your online ID and password. We discourage you from
      giving anyone access to your online ID and password for your Getmyboat account and your email
      account. However, if you do give someone your online ID and online password, or if you fail to
      adequately safeguard such information, you are responsible for any and all transactions that
      the person performs while using your Getmyboat account or your email account, even those
      transactions that are fraudulent or that you did not intend or want performed.
    </p>

    <h3>Exclusion of Warranties</h3>

    <p>
      YOU EXPRESSLY UNDERSTAND AND AGREE THAT THE SERVICES ARE PROVIDED &quot;AS IS&quot; AND
      &quot;AS AVAILABLE&quot; AND WITHOUT WARRANTY OF ANY KIND AND GETMYBOAT ITS OFFICERS,
      DIRECTORS, EMPLOYEES, AGENTS, SUBSIDIARIES, AFFILIATES, LICENSORS AND SUPPLIERS EXPRESSLY
      DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO, THE IMPLIED
      WARRANTIES OF TITLE, MERCHANTABILITY, NON-INFRINGEMENT, AND FITNESS FOR A PARTICULAR PURPOSE.
      NEITHER GETMYBOAT NOR ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, SUBSIDIARIES, AFFILIATES,
      LICENSORS NOR SUPPLIERS WARRANT THAT THE OPERATION OF THE SERVICE WILL BE UNINTERRUPTED, ERROR
      FREE OR WITHOUT BREACHES OF SECURITY.
    </p>

    <p>
      GETMYBOAT, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, SUBSIDIARIES, AFFILIATES, AND ITS
      LICENSORS DO NOT REPRESENT OR WARRANT TO RENTERS OR MEMBERS THAT:
    </p>

    <blockquote>
      <ul>
        <li>THE INFORMATION PROVIDED BY RENTERS OR MEMBERS IS TRUE OR CORRECT,</li>
        <li>
          BOAT RENTALS WILL BE AVAILABLE AT ADVERTISED DATES, TIMES, LOCATIONS, AND/OR PRICES,
          INCLUDING PURSUANT TO A CONFIRMED OR PURCHASED RESERVATION,
        </li>
        <li>BOATS WILL BE COMPLIANT WITH ANY LEGAL OR SAFETY REQUIREMENTS,</li>
        <li>YOUR USE OF THE SERVICES WILL MEET YOUR REQUIREMENTS OR EXPECTATIONS,</li>
        <li>YOUR USE OF THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE OR FREE FROM ERROR,</li>
        <li>
          ANY INFORMATION OBTAINED BY YOU AS A RESULT OF YOUR USE OF THE SERVICES WILL BE ACCURATE
          OR RELIABLE, OR
        </li>
        <li>
          THAT DEFECTS IN THE OPERATION OR FUNCTIONALITY OF ANY SOFTWARE PROVIDED TO YOU AS PART OF
          THE SERVICES WILL BE CORRECTED.
        </li>
      </ul>
    </blockquote>

    <p>
      SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF IMPLIED WARRANTIES, SO IN THESE
      JURISDICTIONS THE ABOVE LIMITATIONS OR EXCLUSIONS MAY NOT APPLY.
    </p>

    <h3>Limitation of Liability</h3>

    <p>
      IN NO EVENT WILL GETMYBOAT ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, SUBSIDIARIES OR
      AFFILIATES BE LIABLE TO RENTERS, MEMBERS, OR ANY PARTY FOR:
    </p>

    <blockquote>
      <ul>
        <li>
          ANY DIRECT, INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, EXEMPLARY, OR PUNITIVE, DAMAGES
          ARISING OUT OF THE USE, AVAILABILITY OR UNAVAILABILITY OF THE SERVICES (INCLUDING BUT NOT
          LIMITED TO THE UNAVAILABILITY OR CHANGE IN ADVERTISED AND/OR PURCHASED BOAT RENTALS) OR
          ANY OTHER SERVICES OR GOODS OBTAINED THROUGH USE OF ANY OF THE FOREGOING, OR ANY DATA
          TRANSMITTED THROUGH THE SERVICE, EVEN IF GETMYBOAT HAS BEEN ADVISED OF THE POSSIBILITY OF
          SUCH DAMAGES; AND
          <ul>
            <li>
              ANY LOSS OR DAMAGE WHICH MAY BE INCURRED BY YOU, INCLUDING BUT NOT LIMITED TO LOSS OR
              DAMAGE AS A RESULT OF:
            </li>
            <li>
              ANY RELIANCE PLACED BY YOU ON THE COMPLETENESS, ACCURACY OR EXISTENCE OF ANY
              ADVERTISING, OR AS A RESULT OF ANY RELATIONSHIP OR TRANSACTION BETWEEN YOU AND ANY
              ADVERTISER OR SPONSOR WHOSE ADVERTISING APPEARS ON THE SERVICES;
            </li>
            <li>
              ANY CHANGES WHICH GETMYBOAT MAY MAKE TO THE SERVICES, OR FOR ANY PERMANENT OR
              TEMPORARY CESSATION IN THE PROVISION OF THE SERVICES (OR ANY FEATURES WITHIN THE
              SERVICES);
            </li>
            <li>YOUR FAILURE TO PROVIDE GETMYBOAT WITH ACCURATE ACCOUNT INFORMATION;</li>
            <li>YOUR FAILURE TO KEEP YOUR PASSWORD OR ACCOUNT DETAILS SECURE AND CONFIDENTIAL.</li>
          </ul>
        </li>
        <li>
          ANY BODILY INJURY, DEATH, OR PROPERTY DAMAGE THAT MAY OCCUR AS THE RESULT OF A BOATING
          RELATED ACCIDENT, THE RESERVATION FOR WHICH WAS FACILITATED USING GETMYBOAT&apos;S
          SERVICES. YOU AGREE THAT GETMYBOAT IS NOT A PARTY TO ANY CONTRACT OR AGREEMENT FOR BOAT
          RENTALS AND IS IN NO WAY RESPONSIBLE FOR INJURIES TO PERSONS OR DAMAGE TO PROPERTY WHICH
          MAY OCCUR DURING OR RELATED TO A BOAT RENTAL WHETHER AS A RESULT OF THE NEGLIGENCE OF ANY
          RENTER, BOAT OPERATOR, OR MEMBER, OR THE SAFETY OF ANY BOAT OR FOR ANY OTHER REASON. YOU
          ACKNOWLEDGE AND AGREE THAT GETMYBOAT SHALL HAVE NO LIABILITY RELATED TO YOUR BOOKING AND
          RENTAL OF A BOAT, AND ANY SUCH BOOKING AND/OR RENTAL IS MADE AT THE RENTER&apos;S AND
          MEMBER&apos;S SOLE RISK.
        </li>
      </ul>
    </blockquote>

    <p>
      Notwithstanding the Terms, if Getmyboat is held liable, Getmyboat&apos;s entire aggregate
      liability under or arising out of these Terms or the Services shall be limited to the greater
      of (a) the service fees you paid to Getmyboat in connection with your use of the Services (if
      any), or (b) One-Hundred Dollars (US$100.00). Some jurisdictions do not allow the exclusion or
      limitation of certain damages, so in these jurisdictions the above limitations or exclusions
      may not apply.
    </p>

    <p>
      <strong>Release and Indemnity. </strong>
      IN THE EVENT OF A DISPUTE BETWEEN A RENTER AND A MEMBER, MEMBER AND RENTER, JOINTLY AND
      SEVERALLY, HEREBY AGREE TO INDEMNIFY, RELEASE, DEFEND AND HOLD HARMLESS GETMYBOAT AND ITS
      DIRECTORS, OFFICERS, AGENTS AND EMPLOYEES (COLLECTIVELY, THE “GETMYBOAT PARTIES”) FROM ANY
      CLAIMS, CAUSES OF ACTION, DEMANDS, RECOVERIES, LOSSES, DAMAGES (ACTUAL AND CONSEQUENTIAL),
      FINES, PENALTIES AND OTHER COSTS OR EXPENSES, OF ANY KIND OR NATURE, INCLUDING BUT NOT LIMITED
      TO REASONABLE LEGAL AND ACCOUNTING FEES (COLLECTIVELY, THE “CLAIMS”), KNOWN AND UNKNOWN,
      ARISING OUT OF OR IN ANY WAY CONNECTED WITH SUCH DISPUTES, INCLUDING, WITHOUT LIMITATION,
      DISPUTES ARISING OVER BREACH OF THIS AGREEMENT, BREACH OF ANY SEPARATE AGREEMENT OR
      TRANSACTION, AND/OR THE SUBSTITUTION OF RENTAL UNITS. FURTHER, MEMBER AND RENTER EACH HEREBY
      AGREE TO INDEMNIFY, RELEASE, DEFEND AND HOLD HARMLESS THE GETMYBOAT PARTIES FROM ANY CLAIMS
      BROUGHT AS A RESULT OF MEMBER AND/OR RENTER&apos;S (1) BREACH OF THE TERMS, (2) VIOLATION OF
      ANY LAW OR THE RIGHTS OF A THIRD PARTY, AND (3) USE OF THE SERVICES. USERS ALSO ACKNOWLEDGE
      AND AGREE THAT GETMYBOAT HAS NO OBLIGATION TO MEDIATE, RESOLVE, OR ADJUDICATE DISPUTES BETWEEN
      USERS EXCEPT AS EXPRESSLY STATED IN THESE TERMS OF USE.
    </p>

    <h3>Proprietary Materials</h3>
    <p>
      The Services contain proprietary materials such as logos, text, software, photos, video,
      graphics, music and sound. Getmyboat is the owner or licensee of all such proprietary material
      and all intellectual property rights therein, including without limitation, copyrights and
      trademarks. In using the Services, you agree not to upload, download, distribute, or reproduce
      in any way any such proprietary materials without obtaining Getmyboat&apos;s prior consent,
      except you may make limited copies of your booking documents (and related documents) for boat
      rentals booked through the Services.
    </p>

    <h3>Unsolicited Ideas and Feedback</h3>
    <p>
      From time to time, users submit to us ideas or suggestions pertaining to our business, such as
      ideas for new or improved products or technologies, website or tool enhancements, processes,
      materials, marketing plans or new product names. We do not solicit such ideas or suggestions
      and are under no obligation to review or consider them. If you choose to submit any ideas,
      original creative artwork, suggestions or other works (&quot;submissions&quot;) in any form to
      us, then regardless of what you say, write or provide to us in connection with your
      submissions, the following terms shall apply. The sole purpose of this policy is to avoid
      potential misunderstandings or disputes in the event that any part of our business, such as
      our products, websites, technologies or marketing strategies, seem similar to any of your
      submissions.
    </p>

    <p>
      If you provide any submissions to us, you agree that: (1) we may use or redistribute any such
      submission and its contents for any purpose and in any way without any compensation to you;
      (2) there is no obligation for us to review any submission; and (3) there is no obligation to
      keep any submission confidential.
    </p>

    <h3>No Spam, Spyware, or Spoofing</h3>
    <p>
      We and our Users do not tolerate spam. You may not use the Site to send spam or otherwise send
      content that would violate the Terms.
    </p>

    <h3>Links to Third-Party Websites or Services</h3>
    <p>
      The Services may contain links to other websites, social media platforms or other online
      services operated by parties other than Getmyboat (&quot;Third-Party Websites&quot;).
      Getmyboat&apos;s links to such other Third-Party Websites is for informational purposes only
      and does not imply any endorsement of the products, services or material on such Third-Party
      Websites or any association with their sponsors or operators. You access any Third-Party
      Websites at your own risk. We are not responsible in any way for such Third-Party Websites or
      resources. We will not have any liability as a result of your use of such Third-Party Websites
      and resources, and your use of such sites and resources will not be governed by these Terms.
    </p>

    <h3>Additional Terms Applicable to Direct Bookings</h3>
    <p>
      <strong>Service Charge. </strong>
      For the Direct Booking Feature, we charge the Renter a service fee
      of 2.5% of the total rental with a minimum of a $5 USD service fee (i.e. the
      booking and any additional expenses such as taxes we are required to collect)
      for facilitating the rental, and this amount will be reflected in the final
      amount charged to Renter&apos;s credit card. We reserve the right to round up amounts
      to the nearest whole dollar (for example, $202.60 will be rounded to $203).
      We will process any necessary currency conversion (Getmyboat charges in
      US dollars unless otherwise specified) based upon Renter&apos;s currency on the
      day the credit card is charged and we will charge a conversion fee
      of 3% of the total rental for currency conversions. We reserve the right to adjust
      the conversion rate based upon when the payout is made to the Member. The service fee and
      any conversion fee constitute &quot;Transaction Fees&quot;. Getmyboat
      reserves the right to adjust its Transaction Fees at any time. Any Fee changes will be
      effective the next time you book a rental after such fee change goes into effect
      (i.e., rentals already booked will be subject to Fees as established at the time of booking).
    </p>

    <p>
      <strong>Monitoring. </strong>
      Under direct bookings, the Member is directly bringing the Renter to the Getmyboat site
      and submitting Renter information. Getmyboat is under no obligation to verify or monitor
      the information residing or transmitted to this site for the Direct Booking Feature.
    </p>

    <p>
      You are required to carefully review the information submitted. Getmyboat makes no warranty
      as to the accuracy, completeness or currency of the content. It is your responsibility to
      verify any information before relying on it. The content of this site may include technical
      inaccuracies or typographical errors. From time to time, changes are made to the
      content herein.
    </p>

    <p>
      <strong>Limitation of Liability. </strong>
      You assume all responsibility and risk for the use of Direct Booking. In no event shall
      Getmyboat, be liable for any direct, special, indirect, or consequential damages or
      any damages whatsoever; including but not limited to loss of use, data or content,
      and transaction conducted through or facilitated by the site, any claim attributable
      to errors, omissions or other inaccuracies in the site, the service and or the content,
      unauthorized access to or alteration of your transmissions or data, or any other matter
      relating the site and or the service.
    </p>

    <p>
      <strong>Terminate or Restrict Access. </strong>
      Getmyboat have the right to terminate or restrict access to any or all of this Direct
      Booking Feature, unilaterally and without notice, in its sole discretion. Getmyboat
      also reserves any and all remedies at law or equity in connection with violation
      of these Terms of Use.
    </p>

    <h3>Other Terms</h3>
    <p>
      <strong>General Compliance with Laws. </strong>
      You shall comply with all applicable laws, statutes, ordinances and regulations regarding your
      use of the Services.
    </p>

    <p>
      <strong>Headings. </strong>
      Headings are for reference purposes only and in no way define, limit, construe or describe the
      scope or extent of such section.
    </p>

    <p>
      <strong>Assignment. </strong>
      This Agreement, the Terms, the Services and any modifications thereto may not be assigned or
      in any way transferred by you without the prior written consent of Getmyboat. Getmyboat may
      assign this Agreement, the Terms, and any information you provide without your consent,
      including, without limitation, in connection with a sale of its business, assets, a merger or
      other similar transaction.
    </p>

    <p>
      <strong>Governing Law. </strong>
      The terms of this Agreement shall be construed in accordance with the substantive laws of
      Atlanta, Georgia, United States of America, without giving effect to the principles of
      conflict or choice of law of such state. The original form of this Agreement has been written
      in English. The parties hereto waive any statute, law, or regulation that might provide an
      alternative law or forum or might require this Agreement to be written in any language other
      than English. This Agreement expressly excludes the United Nations Convention on Contracts for
      the International Sale of Goods.
    </p>

    <p>
      <strong>Choice of Forum and Venue. </strong>
      You hereby consent to the exclusive jurisdiction and venue of state courts in Cobb County of
      Georgia, U.S.A., or the Northern District Court of Georgia, Atlanta Division, in all disputes
      arising out of the Terms or relating to the use of the Service. All arbitration proceedings
      to be held in Atlanta, Georgia.
    </p>

    <p>
      <strong>No Responsibility for Acts of God. </strong>
      Getmyboat shall not be liable for any delay or failure to perform in connection with any
      booking, Service or in the performance of any obligation hereunder, if such delay or failure
      is due to or in any manner caused by acts of God, rebellion, riots, hijacking, insurrection,
      civil commotion, strikes or labor disputes, fires, floods, laws, regulations, acts, demands or
      and orders of any government or agency, seizure of the boat under legal process, adverse
      weather conditions, inability to obtain fuel, boat damage or loss, lack of essential parts or
      supplies, mechanical problems, illness or incapacitation of crew members, denial of operating
      or docking approvals, clearances or permits by governmental authority, or any other cause
      which is beyond the control of Getmyboat. Getmyboat is hereby released by a User from any
      claim or demand for any direct or consequential damages arising out of failure to perform as a
      result of a force majeure event whether or not herein enumerated or other event referenced
      above.
    </p>

    <p>
      <strong>Severability. </strong>
      If any provision of the Terms is found to be invalid, illegal or unenforceable by any court
      having competent jurisdiction, the validity, legality and enforceability of the remaining
      provisions will not in any way be affected or impaired thereby, and the remaining valid,
      legal, and enforceable provisions will be interpreted, to the extent possible, to achieve the
      purposes as originally expressed in the invalid, illegal, or unenforceable provision.
    </p>

    <p>
      <strong>Waiver. </strong>
      No waiver of any of the Terms shall be deemed a waiver of any other such Terms set forth
      herein.
    </p>

    <p>
      <strong>Entire Agreement. </strong>
      The Terms constitute the entire agreement between you and Getmyboat with respect to the
      subject matter covered herein, including use of the Services and any subject matter therein,
      and supersede all previous communications, representations, understandings and agreements,
      either oral or written, between the parties with respect to said subject matter.
    </p>

    <p>
      <strong>Termination. </strong>
      Getmyboat may immediately issue a warning, temporarily suspend, indefinitely suspend or
      terminate User&apos;s use of all or any portion of the Services if you breach the Terms, or at
      any time or for any reason, at the sole discretion of Getmyboat.
    </p>

    <p>
      <strong>Non-Endorsement. </strong>
      Getmyboat does not recommend one Member over another and does not provide advice to users
      regarding which Member to select for the rentals. All comments or reviews posted by Getmyboat
      users, Renters, or Members on our Services do not represent the views of Getmyboat in any way
      and are not endorsed by Getmyboat absent a specific written statement to the contrary.
    </p>

    <p>
      <strong>No Relationship. </strong>
      You agree that no joint venture, partnership, agency or employment relationship exists between
      you and Getmyboat as a result of this Agreement or use of the Services.
    </p>

    <p>
      <strong>Notice of Claim. </strong>
      You agree that all disputes arising out of the Terms or the use of Getmyboat&apos;s Services
      shall be communicated to Getmyboat in writing within thirty (30) days from the date of the
      incident causally related to the dispute. You hereby waive your right to any claim against
      Getmyboat not communicated as provided by this Section.
    </p>

    <p>
      <strong>Contacting Us. </strong>
      If you have any questions, concerns or comments about this Agreement or our Services, please
      {' '}
      <UniversalLink
        href={PATHS.CONTACT_US}
        className={styles.link}
      >
        contact us
      </UniversalLink>
      .
    </p>

    <p>Any rights not expressly granted herein are reserved by Getmyboat.</p>
  </main>
);

export default Terms;
